import React, { useEffect, useState, type RefObject } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { HandIcon } from 'lucide-react';
const EASE = [0.23, 1, 0.32, 1] as const;
/** Distance from the device's bottom edge to the Snak footer controls (▶ · counter · NEXT →). */
const FOOTER_OFFSET = 45;
type Cue = {
  title: string;
  body: string;
};
type PageCue = Cue & {
  /** Finds the element on this chapter where the interaction lives. */
  find: (page: Element) => Element | null;
};
const byText = (root: Element, selector: string, re: RegExp) => Array.from(root.querySelectorAll(selector)).find(el => re.test(el.textContent ?? '')) ?? null;

/** One cue per chapter, pointing at that chapter's interaction. */
const PAGE_CUES: PageCue[] = [{
  title: 'Open the study design',
  body: 'See how MINT was run, and its limitations.',
  find: p => byText(p, 'button', /explore study design/i)
}, {
  title: 'Tap a result',
  body: 'Open the definition, context and limitations.',
  find: p => p.querySelector('[role="button"]')
}, {
  title: 'Switch Skin / Blood',
  body: 'See how each compartment is involved.',
  find: p => p.querySelector('[role="tablist"]')
}, {
  title: 'Compare B0, B1, B2',
  body: 'Watch the blood film change with each class.',
  find: p => p.querySelector('[role="tablist"]')
}, {
  title: 'Choose a compartment',
  body: 'Each response rate counts up as you select it.',
  find: p => p.querySelector('[role="tablist"]')
}, {
  title: 'Step through the mechanism',
  body: 'Tap 1–3, or switch to Clinical evidence.',
  find: p => p.querySelector('ol')
}, {
  title: 'Explore further',
  body: 'Open patient selection, outcomes or management.',
  find: p => p.querySelector('ul button')
}, {
  title: 'Go deeper',
  body: 'Open the full results, evidence and references.',
  find: p => byText(p, 'button', /explore mint results/i)
}];
const TRY_CUE: Cue = {
  title: 'Try it — it’s live',
  body: 'Tap NEXT → to move through the chapters, or ▶ for the guided story.'
};
const NEXT_CUE: Cue = {
  title: 'You’re in the Snak',
  body: 'Tap NEXT → for the next chapter, or ▶ for the guided story.'
};
const SHEET_CUE: Cue = {
  title: 'Reading the evidence',
  body: 'Close the panel to return to the chapter.'
};
type Placement = {
  y: number;
  key: string;
  cue: Cue;
};

/**
 * Side note attached to the device by a hairline. Before the first touch it points at the footer
 * controls; afterwards it follows each chapter's interaction up and down the screen.
 * It reads the Snak's rendered screen only — the Snak itself is not modified.
 */
export function PhoneCue({
  engaged,
  hostRef
}: {
  engaged: boolean;
  hostRef: RefObject<HTMLElement>;
}) {
  const [place, setPlace] = useState<Placement | null>(null);
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const measure = () => {
      const hostRect = host.getBoundingClientRect();
      const footerY = hostRect.height - FOOTER_OFFSET;
      let next: Placement = {
        y: footerY,
        key: 'try',
        cue: TRY_CUE
      };
      if (engaged) {
        next = {
          y: footerY,
          key: 'next',
          cue: NEXT_CUE
        };
        const sheet = host.querySelector('[role="dialog"]');
        const page = host.querySelector('.snak-page');
        const counter = Array.from(host.querySelectorAll('button[aria-label="Open chapter menu"]')).find(b => /^\d{2}/.test(b.textContent?.trim() ?? ''));
        const index = counter ? parseInt(counter.textContent!.trim().slice(0, 2), 10) - 1 : -1;
        if (sheet) {
          next = {
            y: hostRect.height * 0.45,
            key: 'sheet',
            cue: SHEET_CUE
          };
        } else if (page && PAGE_CUES[index]) {
          const target = PAGE_CUES[index].find(page);
          const pageRect = page.getBoundingClientRect();
          if (target) {
            const r = target.getBoundingClientRect();
            const center = r.top + Math.min(r.height, 80) / 2;
            // Only follow the target while it's actually visible in the phone screen.
            if (center > pageRect.top + 12 && center < pageRect.bottom - 12) {
              next = {
                y: center - hostRect.top,
                key: `p${index}`,
                cue: PAGE_CUES[index]
              };
            }
          }
        }
      }
      setPlace(prev => prev && prev.key === next.key && Math.abs(prev.y - next.y) < 2 ? prev : next);
    };
    measure();
    const id = window.setInterval(measure, 200);
    window.addEventListener('resize', measure);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('resize', measure);
    };
  }, [engaged, hostRef]);
  if (!place) return null;
  return <motion.div className="pointer-events-none absolute left-full top-0 hidden w-[220px] items-start xl:flex" aria-hidden="true" initial={false} animate={{
    y: place.y - 11
  }} transition={{
    duration: 0.28,
    ease: EASE
  }} data-id="element-127" data-mp="2ywkznq9nda40">
      <span className="mt-[11px] h-px w-8 shrink-0 bg-teal/60" data-id="element-128" data-mp="1nflcf925plgu" />
      <span className="relative mt-[8px] h-[7px] w-[7px] shrink-0 rounded-full bg-teal" data-id="element-129" data-mp="1sh0tkq17r7ty">
        <span className="cue-ping absolute inset-0 rounded-full bg-teal" data-id="element-130" data-mp="1kvoxalmupw4e" />
      </span>
      <div className="relative pl-3" data-id="element-131" data-mp="1fxwwf248azo2">
        <AnimatePresence mode="wait" initial={false} data-id="element-132" data-mp="1fwvhgmk3dt12">
          <motion.div key={place.key} initial={{
          opacity: 0,
          y: 4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: EASE
        }} data-id="element-133" data-mp="1txog9jhffa9z">
            <p className="text-[14px] font-semibold text-ink" data-id="element-134" data-mp="119aecsn7vtt5">{place.cue.title}</p>
            <p className="mt-1 text-[12.5px] leading-snug text-graphite" data-id="element-135" data-mp="x7xb8qbie4px">{place.cue.body}</p>
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>;
}

/** Compact version for screens where the side note has no room. */
export function PhoneCueInline() {
  return <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] text-teal xl:hidden" data-id="element-136" data-mp="ih7xbg6nybid">
      <HandIcon size={12} strokeWidth={2} aria-hidden="true" data-id="element-137" data-mp="di520nghjjnl" />
      INTERACTIVE · TAP NEXT → OR ▶
    </span>;
}